import React, { useRef, useState, useEffect } from 'react';
import { useHedera } from '../../contexts/HederaContext';
import NFTList from './NFTList';
import './SideMenu.css';

function SideMenu({ isOpen, onClose, selectedSpot, onAssetAdd, assets, onAssetsUpdate, isNewGallery, onNewGalleryViewed }) {
    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [assetType, setAssetType] = useState('image');
    const [convertedUrl, setConvertedUrl] = useState('');
    const [selectedColor, setSelectedColor] = useState('#ffffff');
    const { fetchSvgContent } = useHedera();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [currentNFT, setCurrentNFT] = useState(null);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [isNavigating, setIsNavigating] = useState(false);
    const videoElementRef = useRef(null);

    const colorOptions = [
        { id: 'white', label: 'White', value: '#ffffff' },
        { id: 'black', label: 'Black', value: '#000000' },
        { id: 'gray', label: 'Grey', value: '#808080' },
        { id: 'transparent', label: 'Transparent', value: 'transparent' }
    ];

    const getSpotType = () => {
        return selectedSpot?.startsWith('podium-') ? '3D Model' : 'Image';
    };

    const getAcceptedFiles = () => {
        return selectedSpot?.startsWith('podium-')
            ? '.glb,.gltf'
            : 'image/*,video/*,.mp4,.webm,.mov';
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleRemove = () => {
        setPreviewUrl(null);
        setCurrentNFT(null);
        setCurrentFileIndex(0);
        onAssetAdd(selectedSpot, null);
    };

    // Multi-file navigation helper functions
    const getTotalFiles = (nft) => {
        if (!nft) return 0;
        return 1 + (nft.files?.length || 0);
    };
    
    const getCurrentFile = (nft, fileIndex) => {
        if (!nft) return null;
        
        // Index 0 = main content from metadata.image
        if (fileIndex === 0) {
            return {
                uri: nft.metadata.image,
                type: nft.metadata.type,
                isMain: true
            };
        }
        
        // Index 1+ = files from files array
        const arrayIndex = fileIndex - 1;
        const file = nft.files?.[arrayIndex];
        return file ? { ...file, isMain: false } : null;
    };
    
    const handlePreviousFile = () => {
        if (!currentNFT || isNavigating) return;
        setIsNavigating(true);
        
        const totalFiles = getTotalFiles(currentNFT);
        const newIndex = currentFileIndex === 0 ? totalFiles - 1 : currentFileIndex - 1;
        
        setCurrentFileIndex(newIndex);
        updatePreviewForCurrentFile(currentNFT, newIndex, false); // Don't update scene during navigation
        
        // Debounce navigation
        setTimeout(() => setIsNavigating(false), 100);
    };
    
    const handleNextFile = () => {
        if (!currentNFT || isNavigating) return;
        setIsNavigating(true);
        
        const totalFiles = getTotalFiles(currentNFT);
        const newIndex = (currentFileIndex + 1) % totalFiles;
        
        setCurrentFileIndex(newIndex);
        updatePreviewForCurrentFile(currentNFT, newIndex, false); // Don't update scene during navigation
        
        // Debounce navigation
        setTimeout(() => setIsNavigating(false), 100);
    };

    const updatePreviewForCurrentFile = (nft, fileIndex, updateScene = false) => {
        const currentFile = getCurrentFile(nft, fileIndex);
        
        if (currentFile && selectedSpot) {
            setPreviewUrl(currentFile.uri);
            
            // Convert HCS URLs to direct URLs for preview display
            if (currentFile.uri && currentFile.uri.startsWith('hcs://')) {
                const match = currentFile.uri.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                if (match) {
                    const directUrl = `https://kiloscribe.com/api/inscription-cdn/${match[2]}`;
                    setConvertedUrl(directUrl);
                } else {
                    setConvertedUrl(currentFile.uri);
                }
            } else {
                setConvertedUrl(currentFile.uri);
            }
            
            const newAssetType = currentFile.type?.startsWith('video/') ? 'video' : 
                                currentFile.type?.includes('model') ? 'model' : 
                                currentFile.type === 'text/html' ? 'html' : 'image';
            setAssetType(newAssetType);
            
            // Only update the scene when explicitly requested (not during navigation)
            if (updateScene) {
                const existingAsset = assets[selectedSpot];
                if (existingAsset) {
                    onAssetAdd(selectedSpot, {
                        ...existingAsset,
                        url: currentFile.uri,
                        isVideo: currentFile.type?.startsWith('video/'),
                        isModel: currentFile.type?.includes('model')
                    }, onAssetsUpdate);
                }
            }
        }
    };

    const handleNFTSelect = (nft) => {
        // For 3D models, place them in the scene without requiring a spot
        if (nft.isModel) {
            const modelId = `free-model-${Date.now()}`;
            onAssetAdd(modelId, {
                type: 'model',
                url: nft.modelUrl || nft.url,
                position: [0, 1, -3],
                rotation: [0, 0, 0],
                scale: [0.5, 0.5, 0.5],
                autoRotate: true,
                isModel: true,
                metadata: nft.metadata,
                token_id: nft.token_id,
                serial_number: nft.serial_number
            }, onAssetsUpdate);
            return;
        }

        // For non-models, keep the existing spot-based behavior
        if (selectedSpot) {
            // Extract and process multi-file data from metadata
            const rawFilesArray = nft.metadata?.files || [];
            const processedFilesArray = rawFilesArray.map(file => {
                let processedFile = { ...file };
                
                // Convert HCS URLs to direct URLs
                if (file.uri && file.uri.startsWith('hcs://')) {
                    const match = file.uri.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                    if (match) {
                        processedFile.uri = `https://kiloscribe.com/api/inscription-cdn/${match[2]}`;
                    }
                }
                
                return processedFile;
            });
            
            const hasMultipleFiles = processedFilesArray.length > 0;
            const isVideo = nft.metadata?.type?.startsWith('video/') || false;
            
            console.log('🎯 Multi-file setup:', {
                processedFilesArray,
                hasMultipleFiles,
                isVideo,
                totalFiles: 1 + processedFilesArray.length
            });
            
            // Create enhanced NFT object with multi-file data
            const enhancedNFT = {
                ...nft,
                files: processedFilesArray,
                hasMultipleFiles,
                isVideo,
                metadata: nft.metadata
            };
            
            // Set up multi-file tracking
            setCurrentNFT(enhancedNFT);
            setCurrentFileIndex(0);
            
            // Get current file data
            const currentFile = getCurrentFile(enhancedNFT, 0);
            
            // Get the existing asset to preserve any transforms
            const existingAsset = assets[selectedSpot];
            
            onAssetAdd(selectedSpot, {
                type: 'nft',
                url: currentFile?.uri || nft.url || nft.metadata?.image,
                // Preserve existing transforms or use defaults
                scale: existingAsset?.scale || [1, 1, 1],
                autoRotate: false,
                isModel: false,
                isVideo,
                metadata: nft.metadata,
                files: processedFilesArray,
                hasMultipleFiles,
                token_id: nft.token_id,
                serial_number: nft.serial_number
            }, onAssetsUpdate);

            setPreviewUrl(currentFile?.uri || nft.url || nft.metadata?.image);
            setAssetType(currentFile?.type?.startsWith('video/') ? 'video' : 
                        currentFile?.type?.includes('model') ? 'model' : 'image');
        }
    };

    useEffect(() => {
        if (selectedSpot && assets[selectedSpot]) {
            const asset = assets[selectedSpot];
            setPreviewUrl(asset.url);
            
            // Convert HCS URLs to direct URLs
            if (asset.url && asset.url.startsWith('hcs://')) {
                const match = asset.url.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                if (match) {
                    const directUrl = `https://kiloscribe.com/api/inscription-cdn/${match[2]}`;
                    setConvertedUrl(directUrl);
                } else {
                    setConvertedUrl(asset.url);
                }
            } else {
                setConvertedUrl(asset.url);
            }
            
            const detectedType = asset.isVideo ? 'video' : asset.isModel ? 'model' : asset.type || 'image';
            console.log('🔍 Asset type detection:', {
                assetUrl: asset.url,
                assetType: asset.type,
                isVideo: asset.isVideo,
                isModel: asset.isModel,
                detectedType,
                hasMultipleFiles: asset.hasMultipleFiles
            });
            setAssetType(detectedType);
            
            // If this is an NFT with multiple files, set up the navigation state
            if (asset.type === 'nft' && asset.hasMultipleFiles) {
                const newNFT = {
                    metadata: asset.metadata,
                    files: asset.files,
                    hasMultipleFiles: asset.hasMultipleFiles,
                    token_id: asset.token_id,
                    serial_number: asset.serial_number,
                    isVideo: asset.isVideo,
                    isModel: asset.isModel
                };
                
                // Only reset file index if this is a different NFT
                const isSameNFT = currentNFT && 
                    currentNFT.token_id === newNFT.token_id && 
                    currentNFT.serial_number === newNFT.serial_number;
                
                setCurrentNFT(newNFT);
                if (!isSameNFT) {
                    setCurrentFileIndex(0);
                }
            } else {
                setCurrentNFT(null);
                setCurrentFileIndex(0);
            }
        } else if (selectedSpot && !currentNFT) {
            // Only clear preview if we don't have a current NFT
            setPreviewUrl(null);
            setConvertedUrl('');
            setAssetType('image');
        }
    }, [selectedSpot, assets[selectedSpot]]);

    // Cleanup video elements when switching files
    useEffect(() => {
        return () => {
            if (videoElementRef.current) {
                videoElementRef.current.pause();
                videoElementRef.current.src = '';
                videoElementRef.current.load();
            }
        };
    }, [currentFileIndex, convertedUrl]);

    useEffect(() => {
        const loadPreview = async () => {
            if (!selectedSpot || !assets[selectedSpot]) return;

            const asset = assets[selectedSpot];
            setPreviewUrl(asset.url);
            setAssetType(asset.type);

            try {
                // Handle HCS URLs
                if (asset.url.startsWith('hcs://')) {
                    setIsLoading(true);
                    const match = asset.url.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                    if (match) {
                        const topicId = match[2];
                        console.log('Fetching from Kiloscribe:', topicId);
                        const response = await fetch(`https://kiloscribe.com/api/inscription-cdn/${topicId}`);
                        if (response.ok) {
                            const content = await response.text();
                            console.log('Received content:', content.substring(0, 100));

                            try {
                                // Try to parse as JSON in case it's metadata
                                const metadata = JSON.parse(content);
                                console.log('Parsed metadata:', metadata);
                                if (metadata.image) {
                                    if (metadata.image.startsWith('hcs://')) {
                                        // Handle nested HCS URL
                                        const imageMatch = metadata.image.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                        if (imageMatch) {
                                            const imageTopicId = imageMatch[2];
                                            console.log('Fetching nested image:', imageTopicId);
                                            const imageResponse = await fetch(`https://kiloscribe.com/api/inscription-cdn/${imageTopicId}`);
                                            if (imageResponse.ok) {
                                                const imageContent = await imageResponse.text();
                                                console.log('Received image content:', imageContent.substring(0, 100));
                                                setConvertedUrl(`data:image/svg+xml;base64,${btoa(imageContent)}`);
                                            }
                                        }
                                    } else {
                                        setConvertedUrl(metadata.image);
                                    }
                                } else {
                                    // If no image field, treat content as direct SVG
                                    setConvertedUrl(`data:image/svg+xml;base64,${btoa(content)}`);
                                }
                            } catch (parseError) {
                                console.log('Not JSON, treating as direct content');
                                // Not JSON, treat as direct SVG content
                                setConvertedUrl(`data:image/svg+xml;base64,${btoa(content)}`);
                            }
                        }
                    }
                    setIsLoading(false);
                } else {
                    setConvertedUrl(asset.url);
                }
            } catch (error) {
                console.error('Failed to load preview:', error);
                setIsLoading(false);
            }
        };

        loadPreview();
    }, [selectedSpot, assets]);

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsLoading(true);
        try {
            const url = URL.createObjectURL(file);
            const isModel = selectedSpot?.startsWith('podium-');
            setPreviewUrl(url);
            setAssetType(isModel ? 'model' : 'image');

            // Get the existing asset to preserve any transforms
            const existingAsset = assets[selectedSpot];

            if (isModel) {
                onAssetAdd(selectedSpot, {
                    type: 'model',
                    url,
                    position: existingAsset?.position || [0, 1, -3],
                    rotation: existingAsset?.rotation || [0, 0, 0],
                    scale: existingAsset?.scale || [0.5, 0.5, 0.5],
                    autoRotate: true,
                    isModel: true
                });
            } else {
                onAssetAdd(selectedSpot, {
                    type: 'image',
                    url,
                    // Don't set position/rotation for wall art - use spot defaults
                    scale: existingAsset?.scale || [1, 1, 1],
                    autoRotate: false,
                    isModel: false
                });
            }
        } catch (error) {
            console.error('Failed to process file:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleColorSelect = (colorId) => {
        setSelectedColor(colorId);
        const existingAsset = assets[selectedSpot];
        
        // Update asset with new background color
        const updatedAsset = {
            ...(existingAsset || {}),
            backgroundColor: colorId
        };

        onAssetAdd(selectedSpot, updatedAsset, onAssetsUpdate);
    };

    return (
        <div className={`side-menu-wrapper ${isCollapsed ? 'hidden' : ''}`} data-ui-element>
            <div className="side-menu-content" data-ui-element>
                <div className="menu-header">
                    <h2>Add {getSpotType()}</h2>
                    <p className="spot-id">Spot {selectedSpot}</p>
                </div>

                <div className="preview-section">
                    <div className="preview-box">
                        {isLoading ? (
                            <div className="loading">
                                <div className="loading-spinner"></div>
                                <div>Processing...</div>
                            </div>
                        ) : previewUrl ? (
                            <>
                                {assetType === 'model' ? (
                                    <div className="model-preview">
                                        <div className="model-icon">📦</div>
                                        <div>3D Model Loaded</div>
                                    </div>
                                ) : assetType === 'video' ? (
                                    <video
                                        ref={videoElementRef}
                                        src={convertedUrl}
                                        className="preview-video"
                                        controls
                                        muted
                                        preload="metadata"
                                        onError={(e) => {
                                            console.error('Failed to load video preview:', e);
                                            setPreviewUrl(null);
                                        }}
                                        onLoadedData={() => {
                                            console.log('Video loaded:', convertedUrl);
                                        }}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                ) : getCurrentFile(currentNFT, currentFileIndex)?.type === 'text/html' ? (
                                    <div className="html-placeholder">
                                        <div className="html-icon">📄</div>
                                        <div>HTML Content</div>
                                        <div className="html-note">Preview disabled for performance</div>
                                    </div>
                                ) : (
                                    <img
                                        src={convertedUrl}
                                        alt="Preview"
                                        className="preview-image"
                                        onError={(e) => {
                                            console.error('Failed to load preview:', e);
                                            if (previewUrl.startsWith('hcs://')) {
                                                // If HCS URL fails, try loading directly from Kiloscribe
                                                const match = previewUrl.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                                if (match) {
                                                    const topicId = match[2];
                                                    const directUrl = `https://kiloscribe.com/api/inscription-cdn/${topicId}`;
                                                    console.log('Trying direct URL:', directUrl);
                                                    setConvertedUrl(directUrl);
                                                }
                                            } else {
                                                setPreviewUrl(null);
                                            }
                                        }}
                                    />
                                )}
                                <button className="remove-preview-button" onClick={handleRemove}>×</button>
                            </>
                        ) : (
                            <div className="no-preview">
                                <div className="upload-icon">
                                    {selectedSpot?.startsWith('podium-') ? '🔷' : '🖼'}
                                </div>
                                <div>No {getSpotType()} Selected</div>
                                <div className="upload-hint">Click below to choose a file</div>
                            </div>
                        )}
                    </div>
                    
                    {/* Multi-file navigation */}
                    {currentNFT && getTotalFiles(currentNFT) > 1 && (
                        <div className="multi-file-navigation-main">
                            <button 
                                className={`nav-button prev ${isNavigating ? 'disabled' : ''}`}
                                disabled={isNavigating}
                                onClick={() => {
                                    console.log('🔴 PREV BUTTON CLICKED');
                                    handlePreviousFile();
                                }}
                            >
                                ◀
                            </button>
                            <span className="file-counter" key={`counter-${currentFileIndex}`}>
                                {currentFileIndex + 1}/{getTotalFiles(currentNFT)}
                            </span>
                            <button 
                                className={`nav-button next ${isNavigating ? 'disabled' : ''}`}
                                disabled={isNavigating}
                                onClick={() => {
                                    console.log('🟢 NEXT BUTTON CLICKED');
                                    handleNextFile();
                                }}
                            >
                                ▶
                            </button>
                            <span className="file-type-indicator">
                                {getCurrentFile(currentNFT, currentFileIndex)?.type || 'FILE'}
                            </span>
                            <button 
                                className="apply-to-scene-button"
                                onClick={() => {
                                    console.log('🎯 APPLY TO SCENE CLICKED');
                                    updatePreviewForCurrentFile(currentNFT, currentFileIndex, true); // Update scene
                                }}
                            >
                                Apply to Scene
                            </button>
                        </div>
                    )}
                </div>

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept={getAcceptedFiles()}
                    style={{ display: 'none' }}
                />

                {/* Canvas Color Picker */}
                {selectedSpot && !selectedSpot.startsWith('podium-') && (
                    <div className="menu-section">
                        <h3>Canvas Color</h3>
                        <div className="color-options">
                            {colorOptions.map(color => (
                                <button
                                    key={color.id}
                                    className={`color-option ${selectedColor === color.id ? 'selected' : ''}`}
                                    onClick={() => handleColorSelect(color.id)}
                                    style={{
                                        backgroundColor: color.value,
                                        border: color.id === 'transparent' ? '2px dashed #666' : `1px solid ${color.id === 'black' ? '#666' : '#ddd'}`,
                                        position: 'relative'
                                    }}
                                    title={color.label}
                                >
                                    {color.id === 'transparent' && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                                            backgroundSize: '8px 8px'
                                        }} />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* NFT List */}
                <NFTList onSelect={handleNFTSelect} />
            </div>
            <div
                className={`side-menu-tab ${isNewGallery && isCollapsed ? 'new-gallery-glow' : ''}`}
                onClick={() => {
                    setIsCollapsed(!isCollapsed);
                    if (isNewGallery && isCollapsed && onNewGalleryViewed) {
                        onNewGalleryViewed();
                    }
                }}
                data-ui-element
                title={isNewGallery && isCollapsed ? "Click here to view your NFTs!" : ""}
            >
                {isCollapsed ? '▶' : '◀'}
            </div>
        </div>
    );
}

export default SideMenu; 